import useConfirmationStore from '@gui/store/confirmationStore';
import { getViewer } from 'store/viewerStore';
import { DrawModule } from './DrawingModule';

/**
 * PolygonDrawerManager
 *
 * Responsibilities:
 * - Keep DrawModules alive across UI mount/unmount
 * - Ensure only one drawer is actively editing at a time
 * - Explicitly destroy drawers when their owner is removed
 *
 * IMPORTANT RULE:
 * - Drawers may be STOPPED by UI
 * - Drawers may ONLY be DISPOSED by their owner (via destroy*)
 */
export class PolygonDrawerManager {
    private static instance: PolygonDrawerManager;
    private drawers = new Set<DrawModule>();
    private drawersByKey = new Map<string, DrawModule>(); // Optional map (blockId → DrawModule)
    private activeDrawer: DrawModule | null = null;
    private drawerListeners = new Map<
        DrawModule,
        {
            started: () => void;
            stopped: () => void;
        }
    >();

    private constructor() {}

    static getInstance() {
        if (!PolygonDrawerManager.instance) {
            PolygonDrawerManager.instance = new PolygonDrawerManager();
        }
        return PolygonDrawerManager.instance;
    }

    // ======================================================
    // REGISTRATION
    // ======================================================

    register(drawer: DrawModule, key?: string) {
        this.drawers.add(drawer);

        if (key) {
            this.drawersByKey.set(key, drawer);
        }

        const started = () => this.updateContinuousRender();
        const stopped = () => this.updateContinuousRender();
        drawer.on('started', started);
        drawer.on('stopped', stopped);
        this.drawerListeners.set(drawer, { started, stopped });
        this.updateContinuousRender();
    }

    // Unregister a drawer WITHOUT destroying it (used when UI unmounts):
    unregister(drawer: DrawModule, destroy: boolean = false) {
        // Remove from global set
        this.drawers.delete(drawer);

        // Remove from keyed map if present
        for (const [key, value] of this.drawersByKey) {
            if (value === drawer) {
                this.drawersByKey.delete(key);
                break;
            }
        }

        // Clear active drawer if needed
        if (this.activeDrawer === drawer) {
            this.activeDrawer = null;
        }

        const listeners = this.drawerListeners.get(drawer);
        if (listeners) {
            drawer.off('started', listeners.started);
            drawer.off('stopped', listeners.stopped);
            this.drawerListeners.delete(drawer);
        }

        // 🔥 Optional hard cleanup
        if (destroy) {
            drawer.stop(true);
            drawer.visualEffects.removeSpheres?.();
            drawer.visualEffects.removePolyLine?.();
        }
        this.updateContinuousRender();
    }

    // ======================================================
    // CREATION / ACCESS
    // ======================================================

    // Get or create a persistent drawer by key:
    getOrCreate(key: string, factory: () => DrawModule): DrawModule {
        let drawer = this.drawersByKey.get(key);

        if (!drawer) {
            drawer = factory();
            this.register(drawer, key);
        }

        return drawer;
    }

    getActiveDrawer() {
        return this.activeDrawer;
    }

    // ======================================================
    // ACTIVATION LOGIC (UNCHANGED BEHAVIOR)
    // ======================================================

    // Ensure only one drawer is actively editing:
    async requestActivation(newDrawer: DrawModule): Promise<boolean> {
        // 1️) No active drawer yet → allow immediately
        if (!this.activeDrawer || this.activeDrawer === newDrawer) {
            this.activeDrawer = newDrawer;
            return true;
        }

        // 2️) Active drawer exists but isn't running → switch automatically:
        if (!this.activeDrawer.running) {
            this.activeDrawer = newDrawer;
            return true;
        }

        // 3️) Active drawer is running → confirm the switch:
        const { showConfirmation } = useConfirmationStore.getState();

        return new Promise<boolean>((resolve) => {
            showConfirmation(
                'Switch Active Polygon?',
                "You're already editing another polygon. Do you want to close it and switch?",
                () => {
                    // Confirm:
                    this.activeDrawer?.stop();
                    this.activeDrawer = newDrawer;
                    newDrawer.start?.();
                    resolve(true);
                },
                () => {
                    // Camcel:
                    resolve(false);
                },
                'Yes, Switch',
                'Cancel'
            );
        });
    }

    // ======================================================
    // DESTRUCTION (THE MISSING PIECE)
    // ======================================================

    // Destroy a drawer by key (e.g. when a pipeline block is deleted):
    destroyByKey(key: string) {
        const drawer = this.drawersByKey.get(key);
        if (!drawer) return;

        if (this.activeDrawer === drawer) {
            this.activeDrawer = null;
        }

        drawer.stop();
        drawer.visualEffects.removePolyLine();
        drawer.dispose();

        this.drawersByKey.delete(key);
        this.drawers.delete(drawer);

        const listeners = this.drawerListeners.get(drawer);
        if (listeners) {
            drawer.off('started', listeners.started);
            drawer.off('stopped', listeners.stopped);
            this.drawerListeners.delete(drawer);
        }
        this.updateContinuousRender();
    }

    private updateContinuousRender() {
        const anyRunning = Array.from(this.drawers).some((drawer) => drawer.running);
        getViewer().setContinuousRenderReason?.('drawing', anyRunning);
    }
}
